import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';

import { EFFECT_VALUE_MAX, HIGHLIGHT_SENSITIVITY_DEFAULT } from 'constants/params';
import { FileType, Rotate, ConfigFlags as FileConfigFlags } from 'services/file.service';
import { submitRender as submitRenderServ } from 'services/finalcut.service';
import { getStoryboard, StoryBoardTracks, StoryBoard } from 'services/storyboard.service';
import { getAudioMarkerPresets, getAudioMarkers, MarkerPreset } from 'services/audio.service';
import {
  StoryConfig,
  SelectionMethod,
  setStoryConfig as setStoryConfigServ,
  getStoryConfig,
  SetStoryConfigBody,
} from 'services/story.service';
import {
  ClipConfig,
  ConfigFlags as ClipConfigFlags,
  getClips,
  removeDeletedClips,
  removeDeletedClipsByProjectId,
  DEFAULT_CLIP_CONFIG,
} from 'services/clip.service';
import { createSafeAsyncThunk } from 'store/store.helper';
import { RootState, AppThunk } from 'store/store';
import { getSbTracks, parseClipId, sortClips, isNull } from 'components/app/_helpers/_app.helper';
import { ThumbnailStatus, setClipThumbnails, Thumbnails } from 'components/app/_mediator/clip-thumb/clip-thumb.slice';
import { getCells, markersConversion, markersInitFromBE, sortMarkers } from './editor-markers/editor-markers.helper';
import { BackupReq, addBackup, BackupHandlers, BackupAction } from './backup/backup.slice';
import { createBackup } from './backup/backup.helper';

const ERR_ID = 'editor.slice.ts';
const SLICE_ID = 'editor';

export const EMPTY_CELL: EmptyCell = null;
export const UNLINKED_ID = 0;

export type LinkedGroupId = number; // NOTE: can't be 0
export type ClipsMap = Record<ClipId, Clip | null>;
export type EffectsAdjustment = { brightness: number; contrast: number; volume: number; highlightSensitivity: number };
export type Selector = Record<FileId, ClipId[]>;
export type LinkedGroup = ClipId[];
export type LinkedClips = Record<LinkedGroupId, LinkedGroup>;

export type PanZoomIndex = 0 | 1;
export type ConfigPanel = 'file' | 'clip';
export type ClipAction = 'addClip' | 'deleteClip' | 'resizeLeft' | 'resizeRight';
export type BackupClipListener = { clipId: CellClip; action?: ClipAction };
export type BackupClipPayload = { next: CellClip; prev: CellClip; action?: ClipAction };
export type TitleCreditsAction = 'title' | 'credits' | 'both';

export type RotateListener = { rotate: Rotate; flip: boolean };

export interface ClipIdParsed {
  mediaFileId: number;
  startTime: number | null;
  endTime: number | null;
}

export interface Clip extends ClipIdParsed {
  clipId: ClipId;
  clipGroupId: number;
  config: ClipConfig;
  score?: number;
  highlightId?: number;
  isAutoHighlight?: boolean;
  isPinned?: boolean;
}

export interface Cells {
  duration: EditorTime[];
  width: number[]; // %
}

export interface RangeMarkers {
  startMarkerId: MarkerId | null;
  finishMarkerId: MarkerId | null;
}

export interface HandleMarkers {
  cells?: Cells;
  markers: MarkerId[];
  rangeMarkers?: RangeMarkers;
  selectedMarkerPreset?: MarkerPresetId;
  storyBoard?: Partial<StoryBoard>;
}

export interface HandleClips {
  clipsMap?: Record<ClipId, Clip | null>;
  storyBoard?: Partial<StoryBoard>;
  linkedClips?: LinkedClips;
  selector?: Selector;
  markers?: MarkerId[];
  cells?: Cells;
}

export interface HandleStoryConfig {
  storyConfig: Partial<StoryConfig>;
  titleCreditsAction?: TitleCreditsAction;
  markers?: MarkerId[];
  cells?: Cells;
  storyBoard?: { storyBoard: CellClip[] };
}

export interface HandleClipConfig {
  clipId: ClipId;
  config: Partial<ClipConfig>;
}

export interface State {
  activeClip: CellClip;
  backupListener: { action: BackupAction | null } | null;
  activeMenuClip: CellClip;
  selectedClips: ClipId[];
  clipsMap: ClipsMap | null;
  effectsAdjustment: EffectsAdjustment;
  panZoomKeyFrameIndex: PanZoomIndex;
  panZoomRotateListener: RotateListener;
  storyBoard: StoryBoard;
  selector: Selector | null;
  linkedClips: LinkedClips;
  selectedMarkers: Index[];
  markers: MarkerId[] | null;
  selectedMarkerPreset: MarkerPresetId | null;
  markerPresets: MarkerPreset[];
  cells: Cells | null;
  configPanel: ConfigPanel | null;
  storyConfig: StoryConfig | null;
  highlightsFetchMode: {
    storyId: StoryId | null;
    mode: 'filtered' | 'all';
  };
}

const initialState: State = {
  backupListener: null,
  activeClip: null,
  activeMenuClip: null,
  selectedMarkers: [],
  selectedClips: [],
  clipsMap: null,
  effectsAdjustment: {
    brightness: 0,
    contrast: 0,
    volume: EFFECT_VALUE_MAX,
    highlightSensitivity: HIGHLIGHT_SENSITIVITY_DEFAULT,
  },
  panZoomRotateListener: { rotate: 0, flip: false },
  panZoomKeyFrameIndex: 0,
  selector: null,
  storyBoard: {
    storyBoard: [],
    clipBoard: [],
    discardBoard: [],
  },
  linkedClips: {},
  markers: null,
  selectedMarkerPreset: null,
  markerPresets: [],
  cells: null,
  configPanel: null,
  storyConfig: null,
  highlightsFetchMode: {
    storyId: null,
    mode: 'filtered',
  },
};

/* 
  NOTES: markers:
  - must be unique
  - rounded to six decimal places, e.g. 0.123456Я (see the roundTime)
  - cannot be equal to 0 or equal to the length of the track
  - the distance between the markers cannot be less than the 'MIN_CELL_DUR'
*/

// T H U N K S

export const handleMarkers =
  (payload: BackupReq<BackupHandlers.handleMarkers>): AppThunk =>
  async (dispatch, getState) => {
    const state = getState() as RootState;
    const { storyConfig } = state.editor;
    const { duration } = state.audio;
    if (!storyConfig || !duration) return;

    if (payload.data.markers && !payload.data.cells) {
      payload.data.cells = getCells(payload.data.markers, duration);
    }
    const { markers, storyBoard, selectedMarkerPreset, cells } = payload.data;
    const rangeMarkers = isRangeUpdated(
      payload.data.rangeMarkers ?? getRangeMarkers(markers, state.editor.markers!, storyConfig, duration),
      storyConfig
    );
    if (!payload.backupSkip) {
      const data = getMarkersBackup(state, {
        sbUpdate: !!storyBoard,
        rangeUpdate: !!rangeMarkers,
        markersPresetUpdate: !!selectedMarkerPreset,
      });
      const backup = createBackup(BackupHandlers.handleMarkers, payload, data);
      dispatch(addBackup(backup, payload));
    }
    dispatch(editorSlice.actions.setEditorClips({ markers, storyBoard, cells }));
    if (selectedMarkerPreset) dispatch(setSelectedMarkerPreset(selectedMarkerPreset));
    if (rangeMarkers)
      dispatch(editorSlice.actions.setStoryConfig({ storyConfig: { ...storyConfig, ...rangeMarkers } }));
  };
//

export const handleClips =
  (payload: BackupReq<BackupHandlers.handleClips>): AppThunk =>
  (dispatch, getState) => {
    const duration = (getState() as RootState).audio.duration;
    if (payload.data.markers && !payload.data.cells && duration) {
      payload.data.cells = getCells(payload.data.markers, duration);
    }
    if (!payload.backupSkip) {
      const data = getClipsBackup(getState(), payload.data);
      const backup = createBackup(BackupHandlers.handleClips, payload, data);
      dispatch(addBackup(backup, payload));
    }
    dispatch(editorSlice.actions.setEditorClips(payload.data));
  };
//

export const handleClipConfig =
  (payload: BackupReq<BackupHandlers.handleClipConfig>): AppThunk =>
  (dispatch, getState) => {
    if (!payload.backupSkip) {
      const data = getClipConfigBackup(getState(), payload.data);
      const backup = createBackup(BackupHandlers.handleClipConfig, payload, data);
      dispatch(addBackup(backup, payload));
    }
    dispatch(editorSlice.actions.setClipConfig(payload.data));
  };
//

export const handleStoryConfig =
  (payload: BackupReq<BackupHandlers.handleStoryConfig>): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const duration = state.audio.duration;
    if (payload.data.markers && !payload.data.cells && duration) {
      payload.data.cells = getCells(payload.data.markers, duration);
    }
    if (!payload.backupSkip) {
      const data = getStoryConfigBackup(getState(), payload.data);
      const backup = createBackup(BackupHandlers.handleStoryConfig, payload, data);
      dispatch(addBackup(backup, payload));
    }
    dispatch(editorSlice.actions.setStoryConfig(payload.data));
  };
//

export const fetchMarkers = createSafeAsyncThunk(SLICE_ID + '/fetchMarkers', async (storyId: StoryId, { getState }) => {
  const state = getState() as RootState;
  const { duration } = state.audio;
  const { transitionTime, clipTransitionAlignment } = state.editor.storyConfig!;
  const response = await getAudioMarkers(storyId);
  const markers_0 = markersConversion('fromBE', response.data.result, transitionTime, clipTransitionAlignment);
  const markers_1 = markersInitFromBE(markers_0);
  const selectedMarkerPreset = response.data.beatMarkerPresetId;
  const cells = duration ? getCells(markers_1, duration) : null;
  return { markers: markers_1, selectedMarkerPreset, cells };
});

export const fetchPresets = createSafeAsyncThunk(SLICE_ID + '/fetchPresets', async (audioId: AudioFileId) => {
  const res = await getAudioMarkerPresets(audioId);
  const presets = res.data.result.sort((a, b) => (a.type < b.type ? 1 : -1));
  return presets;
});

export const fetchClips = createSafeAsyncThunk(
  SLICE_ID + '/fetchClips',
  async (
    {
      storyId,
      fileId,
      includeAllHighlights = false,
    }: { storyId: StoryId; fileId?: FileId; includeAllHighlights?: boolean },
    { getState, dispatch }
  ) => {
    const { files } = (getState() as RootState).app__files;
    const response = await getClips({ storyId, fileId, includeAllHighlights });

    const clipsMap: State['clipsMap'] = {};
    const selector: Selector = {};
    const thumbnails: Thumbnails = {};
    const linkedClips: LinkedClips = {};

    const clips: Clip[] = sortClips(
      Object.entries(response.data).map(([clipId, data]) => {
        const { mediaFileId, startTime, endTime } = parseClipId(clipId);
        const { clipGroupId, config, score, highlightId, isAutoHighlight, isPinned } = data;
        // Highlights may not have a config property (it's ignored in the mapper)
        // Provide a default config if missing
        const clipConfig: ClipConfig = config || DEFAULT_CLIP_CONFIG;
        if (!clipConfig.panZoomRotate?.length) clipConfig.panZoomRotate = [null, null];
        return {
          clipId,
          mediaFileId,
          startTime,
          endTime,
          clipGroupId,
          config: clipConfig,
          score,
          highlightId,
          isAutoHighlight,
          isPinned,
        };
      })
    );

    Object.values(files).forEach((file) => {
      if ([FileType.video, FileType.image].includes(file.fileType)) selector[file.id] = [];
    });

    clips.forEach((clip) => {
      const clipId = clip.clipId;
      const thumbnailUrl = response.data[clipId].thumbnailUrl;
      clipsMap[clip.clipId] = clip;
      thumbnails[clipId] = {
        status: thumbnailUrl ? ThumbnailStatus.readyToUse : ThumbnailStatus.init,
        url: thumbnailUrl,
      };
      if (clip.mediaFileId) {
        if (selector[clip.mediaFileId]) selector[clip.mediaFileId].push(clipId);
        else selector[clip.mediaFileId] = [clipId];
      }
      if (clip.clipGroupId) {
        if (linkedClips[clip.clipGroupId]) linkedClips[clip.clipGroupId].push(clipId);
        else linkedClips[clip.clipGroupId] = [clipId];
      }
    });

    dispatch(setClipThumbnails(thumbnails));

    return {
      clipsMap,
      selector,
      linkedClips,
      storyId,
      fileId,
      fetchMode: includeAllHighlights ? ('all' as const) : ('filtered' as const),
    };
  }
);

export const fetchStoryboard = createSafeAsyncThunk(SLICE_ID + '/fetchStoryboard', async (storyId: StoryId) => {
  const response = await getStoryboard(storyId);
  const storyBoard = {};
  getSbTracks().forEach((track) => ((storyBoard as StoryBoard)[track] = response.data[track]));
  return storyBoard as StoryBoard;
});

export const fetchStoryConfig = createSafeAsyncThunk(SLICE_ID + '/fetchStoryConfig', async (storyId: StoryId) => {
  const response = await getStoryConfig(storyId);
  return response.data;
});

export const passStoryConfig = createSafeAsyncThunk(SLICE_ID + '/passStoryConfig', async (body: SetStoryConfigBody) => {
  const response = await setStoryConfigServ(body);
  return response.data;
});

export const cleanDeletedClips = createSafeAsyncThunk(SLICE_ID + '/cleanDeletedClips', async (storyId: StoryId) => {
  await removeDeletedClips(storyId);
});

export const cleanDeletedClipsByProjectId = createSafeAsyncThunk(
  SLICE_ID + '/cleanDeletedClipsByProjectId',
  async (projectId: ProjectId) => {
    await removeDeletedClipsByProjectId(projectId);
  }
);

export const submitRender = createSafeAsyncThunk(SLICE_ID + '/submitRender', async (storyId: StoryId) => {
  const response = await submitRenderServ(storyId);
  return response.data.data;
});

// H E L P E R S

const getClipsBackup = (state: RootState, payload: HandleClips): HandleClips => {
  const { selectedStory } = state.projects;
  const { selector, storyBoard, clipsMap, linkedClips, markers, cells } = state.editor;
  const backup: HandleClips = {};
  if (!selector || !selectedStory || !clipsMap) {
    console.error(`[${ERR_ID}]: getClipsBackup`);
    return backup;
  }
  if (payload.markers) {
    backup.markers = markers!;
    backup.cells = cells!;
  }
  if (payload.clipsMap) {
    const data: ClipsMap = {};
    Object.keys(payload.clipsMap).forEach((clipId) => {
      const clipData = clipsMap[clipId] ? { ...clipsMap[clipId] } : null;
      data[clipId] = clipData as Clip;
    });
    backup.clipsMap = data;
  }
  if (payload.storyBoard) {
    const data: Partial<StoryBoard> = {};
    (Object.keys(payload.storyBoard) as StoryBoardTracks[]).forEach((track) => {
      data[track] = storyBoard[track];
    });
    backup.storyBoard = data;
  }
  if (payload.linkedClips) {
    const data: LinkedClips = {};
    Object.keys(payload.linkedClips).forEach((linkedGroupId) => {
      const id = Number(linkedGroupId);
      data[id] = linkedClips[id] ?? [];
    });
    backup.linkedClips = data;
  }
  if (payload.selector) {
    const data: Selector = {};
    Object.keys(payload.selector).forEach((fileId) => {
      const id = Number(fileId);
      data[id] = selector[id] ?? [];
    });
    backup.selector = data;
  }
  return backup;
};

const getMarkersBackup = (
  state: RootState,
  options: { sbUpdate?: boolean; rangeUpdate?: boolean; markersPresetUpdate?: boolean }
): HandleMarkers => {
  const storyBoard = state.editor.storyBoard;
  const storyConfig = state.editor.storyConfig;

  let rangeMarkers = {};
  let selectedMarkerPreset = {};

  if (options.rangeUpdate && storyConfig)
    rangeMarkers = {
      rangeMarkers: {
        finishMarkerId: storyConfig.finishMarkerId,
        startMarkerId: storyConfig.startMarkerId,
      },
    };
  if (options.markersPresetUpdate) selectedMarkerPreset = { selectedMarkerPreset: state.editor.selectedMarkerPreset };

  return {
    storyBoard: options.sbUpdate ? storyBoard : undefined,
    markers: state.editor.markers!,
    cells: state.editor.cells!,
    ...selectedMarkerPreset,
    ...rangeMarkers,
  };
};

const getClipConfigBackup = (state: RootState, payload: HandleClipConfig): HandleClipConfig => {
  const clipId = payload.clipId;
  const backup: HandleClipConfig = { clipId, config: {} };
  const curr = state.editor.clipsMap?.[clipId]?.config;
  if (!curr) {
    console.error(`[${ERR_ID}]: getClipConfigBackup`);
    return backup;
  }
  (Object.keys(payload.config) as (keyof ClipConfig)[]).forEach((key) => {
    backup.config[key] = curr[key] as ClipConfigFlags[] & [null, null] & number;
  });
  return backup;
};

const getStoryConfigBackup = (state: RootState, payload: HandleStoryConfig): HandleStoryConfig => {
  const { markers, cells, storyBoard } = state.editor;
  const backup: HandleStoryConfig = {
    titleCreditsAction: payload.titleCreditsAction,
    storyConfig: {},
  };
  const curr = state.editor.storyConfig;
  if (!curr || !markers || !cells) {
    console.error(`[${ERR_ID}]: getStoryConfigBackup`);
    return backup;
  }
  (Object.keys(payload.storyConfig) as (keyof StoryConfig)[]).forEach((key) => {
    backup.storyConfig[key] = curr[key] as string & number & null;
  });
  if (payload.markers) {
    backup.markers = markers;
    backup.cells = cells;
  }
  if (payload.storyBoard) backup.storyBoard = { storyBoard: storyBoard.storyBoard };
  return backup;
};

const getRangeMarkers = (
  markers: MarkerId[],
  oldMarkers: MarkerId[],
  config: StoryConfig,
  duration: Duration
): RangeMarkers => {
  const getRangeId = (markerId: MarkerId | null) => {
    if (markerId === null) return null;
    if (markers[markerId] === oldMarkers[markerId]) return markerId;

    // search for the nearest marker location
    const time = oldMarkers[markerId];
    const i = markers.findIndex((marker) => time <= marker);
    const index = i === -1 ? markers.length : i;
    const isStart = index === 0;
    const isEnd = index === markers.length;
    const leftTime = time - (isStart ? 0 : markers[index - 1]);
    const rightTime = (isEnd ? duration : markers[index]) - time;
    return leftTime <= rightTime ? (isStart ? null : index - 1) : isEnd ? null : index;
  };
  return {
    startMarkerId: getRangeId(config.startMarkerId),
    finishMarkerId: getRangeId(config.finishMarkerId),
  };
};

const isRangeUpdated = (range: RangeMarkers, config: StoryConfig) => {
  return range.startMarkerId !== config.startMarkerId || range.finishMarkerId !== config.finishMarkerId ? range : false;
};

// S E L E C T O R S

export const selectFileClips = createSelector(
  (state: RootState) => state.editor.selector,
  (state: RootState) => state.projects.selectedFile,
  (selector, selectedFile) => {
    if (!selectedFile || !selector) return;
    const fileClips = selector[selectedFile];
    if (!fileClips) {
      console.error('[clips.slice.ts] selectFileClips');
      return;
    }
    return fileClips;
  }
);

// Intermediate selectors for grouping related state
const selectEditorState = createSelector(
  (state: RootState) => state.editor.clipsMap,
  (state: RootState) => state.editor.highlightsFetchMode,
  (state: RootState) => state.editor.effectsAdjustment,
  (state: RootState) => state.editor.configPanel,
  (state: RootState) => state.editor.storyConfig,
  (clipsMap, highlightsFetchMode, effectsAdjustment, configPanel, storyConfig) => ({
    clipsMap,
    highlightsFetchMode,
    effectsAdjustment,
    configPanel,
    storyConfig,
  })
);

const selectProjectState = createSelector(
  (state: RootState) => state.projects.selectedStory,
  (state: RootState) => state.projects.selectedFile,
  (selectedStory, selectedFile) => ({ selectedStory, selectedFile })
);

/**
 * Selects file clips filtered by highlight sensitivity and selection method
 * - When SelectionMethod=Manual: Hides all highlights (shows only manual clips)
 * - When Flag=false: Hides all highlights (shows only manual clips)
 * - When Flag=true and mode='all': Filters highlights by threshold client-side
 * - When Flag=true and mode='filtered': Returns as-is (already filtered by backend)
 */
export const selectFilteredFileClips = createSelector(
  selectFileClips,
  selectEditorState,
  (state: RootState) => state.app__files.configs,
  selectProjectState,
  (clips, editorState, configs, projectState) => {
    const { clipsMap, highlightsFetchMode, effectsAdjustment, configPanel, storyConfig } = editorState;
    const { selectedStory, selectedFile } = projectState;

    if (!clips || !clipsMap || !selectedFile) return clips;

    const fileConfig = configs[selectedFile];
    if (!fileConfig) return clips;

    // If selection method is Manual, hide all auto highlights
    if (storyConfig?.selectionMethod === SelectionMethod.manual) {
      return clips.filter((clipId) => {
        const clip = clipsMap[clipId];
        return clip && !clip.isAutoHighlight;
      });
    }

    const isSensitivityEnabled = fileConfig.flags?.includes(FileConfigFlags.highlightSensitivity);

    // If flag is off, hide all highlights
    if (!isSensitivityEnabled) {
      return clips.filter((clipId) => {
        const clip = clipsMap[clipId];
        return clip && !clip.isAutoHighlight;
      });
    }

    // If we don't have all highlights loaded, return as-is (already filtered by backend)
    if (highlightsFetchMode.storyId !== selectedStory || highlightsFetchMode.mode !== 'all') {
      return clips;
    }

    // We have all highlights loaded, filter client-side by threshold
    // Use effectsAdjustment value when config panel is open and file is active (for real-time preview)
    const isAdjustment = configPanel === 'file';
    const sensitivity = isAdjustment ? effectsAdjustment.highlightSensitivity : fileConfig.highlightSensitivity;
    const threshold = 100 - sensitivity;

    return clips.filter((clipId) => {
      const clip = clipsMap[clipId];
      if (!clip) return false;

      // Manual clips always included
      if (!clip.isAutoHighlight) return true;

      // Filter highlights by score threshold
      if (clip.score == null) return true;
      return clip.score >= threshold;
    });
  }
);

export const selectStats = createSelector(
  (state: RootState) => state.editor.selector,
  (state: RootState) => state.editor.markers,
  (state: RootState) => state.editor.clipsMap,
  (selector, markers, clipsMap) => {
    if (!markers || !selector) return;
    // Count only pinned highlights (or non-highlight clips)
    const numberOfClips = Object.values(selector).reduce((prev, fileClips) => {
      return (
        prev +
        fileClips.filter((clipId) => {
          const clip = clipsMap?.[clipId];
          // Include if it's manually added, or if it's a pinned highlight
          return clip && (!clip.isAutoHighlight || clip.isPinned === true);
        }).length
      );
    }, 0);
    const max = (markers.length + 1) * Object.keys(StoryBoardTracks).length;
    const available = max - numberOfClips;
    return { numberOfClips, max, available };
  }
);

export const selectClipData = createSelector(
  (state: RootState) => state.editor.activeClip,
  (state: RootState) => state.editor.clipsMap,
  (activeClip, clipsMap): Clip | undefined => {
    if (!clipsMap || !activeClip) return;
    return clipsMap[activeClip] as Clip;
  }
);

// S L I C E

export const editorSlice = createSlice({
  name: SLICE_ID,
  initialState,
  reducers: {
    setActiveClip: (state, action: PayloadAction<State['activeClip']>) => {
      state.activeClip = action.payload;
    },
    setActiveMenuClip: (state, action: PayloadAction<State['activeMenuClip']>) => {
      state.activeMenuClip = action.payload;
    },
    setSelectedClips: (state, action: PayloadAction<State['selectedClips']>) => {
      state.selectedClips = action.payload;
    },
    setEditorClips: (state, action: PayloadAction<HandleClips>) => {
      const { markers, cells, clipsMap, storyBoard, linkedClips, selector } = action.payload;
      if (clipsMap) {
        Object.entries(clipsMap).forEach(([clipId, data]) => {
          if (!state.clipsMap) return;
          if (isNull(data)) delete state.clipsMap[clipId];
          else state.clipsMap[clipId] = data;
        });
      }
      if (storyBoard) {
        (Object.keys(storyBoard) as StoryBoardTracks[]).forEach((track) => {
          state.storyBoard[track] = storyBoard[track] ?? [];
        });
      }
      if (markers) state.markers = markers;
      if (linkedClips) {
        Object.entries(linkedClips).forEach(([linkId, data]) => {
          const id = Number(linkId);
          if (data.length === 0) delete state.linkedClips[id];
          else state.linkedClips[id] = data;
        });
      }
      if (selector) state.selector = { ...state.selector, ...selector };
      if (cells) state.cells = cells;
    },
    setClipConfig: (state, action: PayloadAction<HandleClipConfig>) => {
      const { clipId, config } = action.payload;
      const curr = state.clipsMap?.[clipId]?.config;
      if (!curr) return;
      state.clipsMap![clipId]!.config = { ...curr, ...config };
    },
    setStoryConfig: (state, action: PayloadAction<HandleStoryConfig>) => {
      if (!state.storyConfig) return;
      const { storyConfig, markers, cells, storyBoard } = action.payload;
      state.storyConfig = { ...state.storyConfig, ...storyConfig };
      if (storyBoard) state.storyBoard.storyBoard = storyBoard.storyBoard;
      if (markers) state.markers = markers;
      if (cells) state.cells = cells;
    },
    setSelectedMarkers: (state, action: PayloadAction<Index[]>) => {
      state.selectedMarkers = sortMarkers([...action.payload]);
    },
    setCells: (state, action: PayloadAction<State['cells']>) => {
      state.cells = action.payload;
    },
    setSelectedMarkerPreset: (state, action: PayloadAction<MarkerPresetId>) => {
      state.selectedMarkerPreset = action.payload;
    },
    setConfigPanel: (state, action: PayloadAction<State['configPanel']>) => {
      state.configPanel = action.payload;
    },
    setEffectsAdjustment: (state, action: PayloadAction<Partial<EffectsAdjustment>>) => {
      state.effectsAdjustment = { ...state.effectsAdjustment, ...action.payload };
    },
    setPanZoomKeyFrameIndex: (state, action: PayloadAction<PanZoomIndex>) => {
      state.panZoomKeyFrameIndex = action.payload;
    },
    setPanZoomRotateListener: (state, action: PayloadAction<RotateListener>) => {
      state.panZoomRotateListener = action.payload;
    },
    setBackupListener: (state, action: PayloadAction<BackupAction | undefined>) => {
      state.backupListener = { action: action.payload ?? null };
    },
    resetEditor: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMarkers.fulfilled, (state, action) => {
      if (!action.payload) return;
      const { markers, selectedMarkerPreset, cells } = action.payload;
      state.markers = markers;
      state.selectedMarkerPreset = selectedMarkerPreset;
      state.cells = cells;
    });
    builder.addCase(fetchPresets.fulfilled, (state, action) => {
      state.markerPresets = action.payload;
    });
    builder.addCase(fetchClips.fulfilled, (state, action) => {
      if (action.payload.fileId) {
        // Merge mode (for single file)
        const existingClipsMap = state.clipsMap || {};
        state.clipsMap = { ...existingClipsMap, ...action.payload.clipsMap };

        // Downgrade to highlightsFetchMode'filtered' if per-file fetch was filtered
        if (
          action.payload.storyId &&
          action.payload.fetchMode === 'filtered' &&
          state.highlightsFetchMode.storyId === action.payload.storyId &&
          state.highlightsFetchMode.mode === 'all'
        ) {
          state.highlightsFetchMode = {
            storyId: action.payload.storyId,
            mode: 'filtered',
          };
        }

        // Merge selector - append to existing arrays
        if (!state.selector) {
          state.selector = {};
        }
        Object.entries(action.payload.selector).forEach(([fileId, clipIds]) => {
          const fileIdNum = Number(fileId);
          const existing = state.selector![fileIdNum] || [];
          state.selector![fileIdNum] = [...existing, ...clipIds];
        });

        // Merge linkedClips - append to existing arrays
        Object.entries(action.payload.linkedClips).forEach(([groupId, clipIds]) => {
          const groupIdNum = Number(groupId);
          const existing = state.linkedClips[groupIdNum] || [];
          state.linkedClips[groupIdNum] = [...existing, ...clipIds];
        });
      } else {
        // Replace mode (for all clips)
        state.clipsMap = action.payload.clipsMap;
        state.selector = action.payload.selector;
        state.linkedClips = action.payload.linkedClips;
        if (action.payload.storyId) {
          state.highlightsFetchMode = {
            storyId: action.payload.storyId,
            mode: action.payload.fetchMode || 'filtered',
          };
        }
      }
    });
    builder.addCase(fetchStoryConfig.fulfilled, (state, action) => {
      state.storyConfig = action.payload;
    });
  },
});

export const {
  setActiveClip,
  setActiveMenuClip,
  setSelectedClips,
  setEffectsAdjustment,
  setPanZoomKeyFrameIndex,
  setPanZoomRotateListener,
  setSelectedMarkers,
  setStoryConfig,
  resetEditor,
  setCells,
  setSelectedMarkerPreset,
  setBackupListener,
  setConfigPanel,
} = editorSlice.actions;

export default editorSlice.reducer;
